import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  addTreatment,
  deleteTreatment,
  updateTreatment,
} from '../features/patients/patientSlice';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Medication as MedicationIcon,
  LocalHospital as HospitalIcon,
  Science as LabIcon,
  Chat as ChatIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: theme.palette.background.paper,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: theme.palette.primary.main,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: -4,
    top: 24,
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const treatmentTypes = [
  { value: 'medication', label: 'Medication', icon: <MedicationIcon /> },
  { value: 'therapy_session', label: 'Therapy Session', icon: <ChatIcon /> },
  { value: 'lab_test', label: 'Lab Test', icon: <LabIcon /> },
  { value: 'procedure', label: 'Procedure', icon: <HospitalIcon /> },
  { value: 'follow_up', label: 'Follow-up', icon: <EventIcon /> },
  { value: 'other', label: 'Other', icon: <HospitalIcon /> },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'overdue':
      return 'error';
    case 'upcoming':
      return 'warning';
    case 'future':
      return 'info';
    default:
      return 'success';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'overdue':
      return <WarningIcon />;
    case 'upcoming':
      return <ScheduleIcon />;
    case 'future':
      return <EventIcon />;
    default:
      return <CheckCircleIcon />;
  }
};

const getTreatmentStatus = (status) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'completed':
      return 'primary';
    case 'discontinued':
      return 'error';
    default:
      return 'default';
  }
};

const TreatmentHistory = ({ patientId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { treatments } = useSelector((state) => state.patients);
  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [treatmentData, setTreatmentData] = useState({
    type: '',
    date: new Date(),
    provider: '',
    diagnosis: '',
    treatment: '',
    notes: '',
    followUpDate: null,
  });

  const handleOpenDialog = (treatment = null) => {
    if (treatment) {
      setEditingTreatment(treatment);
      setTreatmentData({
        ...treatment,
        date: new Date(treatment.date),
        followUpDate: treatment.followUpDate
          ? new Date(treatment.followUpDate)
          : null,
      });
    } else {
      setEditingTreatment(null);
      setTreatmentData({
        type: '',
        date: new Date(),
        provider: '',
        diagnosis: '',
        treatment: '',
        notes: '',
        followUpDate: null,
      });
    }
    setTreatmentDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setTreatmentDialogOpen(false);
    setEditingTreatment(null);
    setTreatmentData({
      type: '',
      date: new Date(),
      provider: '',
      diagnosis: '',
      treatment: '',
      notes: '',
      followUpDate: null,
    });
  };

  const handleSubmit = async () => {
    if (!treatmentData.type || !treatmentData.date) return;

    try {
      if (editingTreatment) {
        // Update existing treatment
        await dispatch(
          updateTreatment({
            patientId,
            treatmentId: editingTreatment.id,
            treatmentData,
          })
        ).unwrap();
      } else {
        // Add new treatment
        await dispatch(
          addTreatment({
            patientId,
            treatmentData,
          })
        ).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save treatment:', error);
    }
  };

  const handleDelete = async (treatmentId) => {
    try {
      await dispatch(deleteTreatment({ patientId, treatmentId })).unwrap();
    } catch (error) {
      console.error('Failed to delete treatment:', error);
    }
  };

  const getTreatmentTypeLabel = (type) => {
    const treatmentType = treatmentTypes.find((t) => t.value === type);
    return treatmentType ? treatmentType.label : 'Other';
  };

  const getTreatmentTypeIcon = (type) => {
    const treatmentType = treatmentTypes.find((t) => t.value === type);
    return treatmentType ? treatmentType.icon : <HospitalIcon />;
  };

  return (
    <StyledPaper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h5'>Treatment History</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          Add Treatment
        </Button>
      </Box>

      {treatments.error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {treatments.error}
        </Alert>
      )}

      {treatments.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : treatments.data.length === 0 ? (
        <Alert severity='info'>
          No treatment history available. Click the add button to create a new
          treatment record.
        </Alert>
      ) : (
        <List>
          {treatments.data
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((treatment) => (
              <TimelineItem key={treatment.id}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant='subtitle1' gutterBottom>
                      {getTreatmentTypeLabel(treatment.type)}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {format(new Date(treatment.date), 'MMM d, yyyy')}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      onClick={() => handleOpenDialog(treatment)}
                      sx={{ mr: 1 }}
                    >
                      {getTreatmentTypeIcon(treatment.type)}
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleDelete(treatment.id)}
                      color='error'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  {treatment.provider && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body2' color='text.secondary'>
                        Provider: {treatment.provider}
                      </Typography>
                    </Grid>
                  )}
                  {treatment.diagnosis && (
                    <Grid item xs={12}>
                      <Typography variant='body2' color='text.secondary'>
                        Diagnosis: {treatment.diagnosis}
                      </Typography>
                    </Grid>
                  )}
                  {treatment.treatment && (
                    <Grid item xs={12}>
                      <Typography variant='body2' color='text.secondary'>
                        Treatment: {treatment.treatment}
                      </Typography>
                    </Grid>
                  )}
                  {treatment.notes && (
                    <Grid item xs={12}>
                      <Typography variant='body2' color='text.secondary'>
                        Notes: {treatment.notes}
                      </Typography>
                    </Grid>
                  )}
                  {treatment.followUpDate && (
                    <Grid item xs={12}>
                      <Chip
                        icon={getStatusIcon(
                          getTreatmentStatus(treatment.followUpDate)
                        )}
                        label={`Follow-up: ${format(new Date(treatment.followUpDate), 'MMM d, yyyy')}`}
                        color={getStatusColor(
                          getTreatmentStatus(treatment.followUpDate)
                        )}
                        size='small'
                      />
                    </Grid>
                  )}
                </Grid>
              </TimelineItem>
            ))}
        </List>
      )}

      <Dialog
        open={treatmentDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {editingTreatment ? 'Edit Treatment' : 'Add Treatment'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Treatment Type</InputLabel>
                <Select
                  value={treatmentData.type}
                  onChange={(e) =>
                    setTreatmentData({ ...treatmentData, type: e.target.value })
                  }
                  label='Treatment Type'
                >
                  {treatmentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {type.icon}
                        <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='date'
                label='Date'
                value={format(treatmentData.date, 'yyyy-MM-dd')}
                onChange={(e) =>
                  setTreatmentData({
                    ...treatmentData,
                    date: new Date(e.target.value),
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Provider'
                value={treatmentData.provider}
                onChange={(e) =>
                  setTreatmentData({
                    ...treatmentData,
                    provider: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='date'
                label='Follow-up Date'
                value={
                  treatmentData.followUpDate
                    ? format(treatmentData.followUpDate, 'yyyy-MM-dd')
                    : ''
                }
                onChange={(e) =>
                  setTreatmentData({
                    ...treatmentData,
                    followUpDate: e.target.value
                      ? new Date(e.target.value)
                      : null,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Diagnosis'
                value={treatmentData.diagnosis}
                onChange={(e) =>
                  setTreatmentData({
                    ...treatmentData,
                    diagnosis: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Treatment'
                value={treatmentData.treatment}
                onChange={(e) =>
                  setTreatmentData({
                    ...treatmentData,
                    treatment: e.target.value,
                  })
                }
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Notes'
                value={treatmentData.notes}
                onChange={(e) =>
                  setTreatmentData({ ...treatmentData, notes: e.target.value })
                }
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            disabled={
              !treatmentData.type || !treatmentData.date || treatments.loading
            }
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            {treatments.loading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
};

export default TreatmentHistory;
